import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Button, Card, Picker, Toast } from 'antd-mobile';
import type { PickerColumn } from 'antd-mobile/es/components/picker';
import { DownOutline } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import { getBottomNavBarContainerStyle } from '../../../utils/deviceUtils';
import { useQuery, useMutation, ApolloError } from '@apollo/client';
import { GET_DELIVERY_TASK_ORDER_BY_TRACKING_NUMBER, GetDeliveryTaskOrderByTrackingNumberResponse } from '../graphql/queries/getDeliveryTaskOrderByTrackingNumber';
import { CREATE_ADDRESS_COORDINATE_FEEDBACK, CreateAddressCoordinateFeedbackInput } from '../graphql/mutations/createAddressCoordinateFeedback';
import {
    GET_ADDRESS_LOCATION_TYPE_DICT_CONFIG,
    GetAddressLocationTypeDictConfigResponse,
    AddressLocationTypeDictItem,
} from '../../../graphql/queries/getAddressLocationTypeDictConfig';
import { formatStopNumberFromDeliveryTaskOrder } from '../../../utils/stopNumberUtils';
import AddressCoordinateFeedbackMapView from './components/AddressCoordinateFeedbackMapView';

function getQueryString(locationSearch: string, key: string): string | undefined {
    const value = new URLSearchParams(locationSearch).get(key);
    return value == null || value === '' ? undefined : value;
}

const DEFAULT_MAPBOX_API_KEY = "pk.eyJ1IjoibGl4aWFvaHVpdGl0YSIsImEiOiJjbWNsc2J3aTcwZDkyMmtwdGxiZW95YWt6In0.qoTeK6P5iQ8Mq62KdImwBQ";

const AddressCoordinateFeedbackNativePage: React.FC = () => {
    const location = useLocation();
    const params = useParams<{ trackingNumber?: string }>();
    const { t, i18n } = useTranslation();
    const trackingNumber = params.trackingNumber ?? getQueryString(location.search, 'trackingNumber');

    const { data: trackingData, loading: isDataLoading } = useQuery<GetDeliveryTaskOrderByTrackingNumberResponse>(
        GET_DELIVERY_TASK_ORDER_BY_TRACKING_NUMBER,
        {
            variables: { trackingNumber },
            skip: !trackingNumber
        }
    );
    const deliveryTaskOrder = trackingData?.getDeliveryTaskOrderByTrackingNumber;
    const [originalLatitude, setOriginalLatitude] = useState<number | null>(null);
    const [originalLongitude, setOriginalLongitude] = useState<number | null>(null);
    const [selectedLatitude, setSelectedLatitude] = useState<number | null>(null);
    const [selectedLongitude, setSelectedLongitude] = useState<number | null>(null);
    const [driverLatitude, setDriverLatitude] = useState<number | null>(null);
    const [driverLongitude, setDriverLongitude] = useState<number | null>(null);
    const [createFeedback, { loading: isSubmitting }] = useMutation(CREATE_ADDRESS_COORDINATE_FEEDBACK);
    // 使用 useRef 保存当前提交的 requestId，确保弱网重复调用时使用相同的 requestId
    const currentRequestIdRef = useRef<string | null>(null);

    const { data: typeDictData } = useQuery<GetAddressLocationTypeDictConfigResponse>(
        GET_ADDRESS_LOCATION_TYPE_DICT_CONFIG,
        { fetchPolicy: 'cache-first' }
    );
    const typeDictItems: AddressLocationTypeDictItem[] = useMemo(
        () => (typeDictData?.getAddressLocationTypeDictConfig ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
        [typeDictData]
    );
    const [selectedSecondaryCode, setSelectedSecondaryCode] = useState<string | null>(null);
    const [typePickerVisible, setTypePickerVisible] = useState(false);

    const currentLang = (i18n.language || 'zh').toLowerCase();
    const pickDropdownName = (item: AddressLocationTypeDictItem) => {
        if (currentLang.startsWith('en')) return item.dropdownNameEn;
        if (currentLang.startsWith('es')) return item.dropdownNameEs;
        return item.dropdownNameZh;
    };

    // 二级 code 全局唯一，单列 Picker 展示下拉文案
    const typePickerColumns: PickerColumn[] = useMemo(() => {
        return [
            typeDictItems.map((item) => ({
                label: pickDropdownName(item),
                value: item.secondaryCode,
            })),
        ];
    }, [typeDictItems, currentLang]);

    const selectedTypeItem = useMemo(
        () => typeDictItems.find((it) => it.secondaryCode === selectedSecondaryCode) ?? null,
        [typeDictItems, selectedSecondaryCode]
    );
    const selectedTypeLabel = selectedTypeItem ? pickDropdownName(selectedTypeItem) : '';

    // 获取司机当前GPS定位
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setDriverLatitude(position.coords.latitude);
                    setDriverLongitude(position.coords.longitude);
                },
                (error) => {
                    console.warn('获取司机定位失败:', error);
                }
            );
        }
    }, []);

    // 所有数据都从 deliveryTaskOrder 接口获取
    const deliveryAddress = deliveryTaskOrder?.orderWithAddress?.deliveryAddress;
    const orderAddressId = deliveryAddress?.id;
    
    // 从 URL query 获取 isFromCluster 标识（原生调用专用）
    const isFromCluster = getQueryString(location.search, 'isFromCluster') === 'true';

    // 根据来源决定地址显示方式
    const address = deliveryAddress ? (
        isFromCluster
            ? // 聚合页：优先使用标准地址，没有则拼接
              deliveryAddress.standardizedAddressString || [
                  deliveryAddress.addressLine1 || '',
                  deliveryAddress.addressLine2 || '',
                  deliveryAddress.city || '',
                  deliveryAddress.state || '',
                  deliveryAddress.zipcode || ''
              ].filter(Boolean).join(' ')
            : // 单页：直接拼接（不加 US）
              `${deliveryAddress.addressLine1 || ''} ${deliveryAddress.addressLine2 || ''} ${deliveryAddress.city || ''} ${deliveryAddress.state || ''} ${deliveryAddress.zipcode || ''}`.trim()
    ) : '';

    // 用于地理编码的地址格式，增加国家代码以提高识别率
    const geocodingAddress = deliveryAddress ? (
        isFromCluster
            ? // 聚合页：优先使用标准地址，没有则拼接
              deliveryAddress.standardizedAddressString || [
                  deliveryAddress.addressLine1 || '',
                  deliveryAddress.addressLine2 || '',
                  deliveryAddress.city || '',
                  deliveryAddress.state || '',
                  deliveryAddress.zipcode || '',
                  'US'
              ].filter(Boolean).join(' ')
            : // 单页：直接拼接（包含 US 以提高地理编码识别率）
              [
                  deliveryAddress.addressLine1 || '',
                  deliveryAddress.addressLine2 || '',
                  deliveryAddress.city || '',
                  deliveryAddress.state || '',
                  deliveryAddress.zipcode || '',
                  'US'
              ].filter(Boolean).join(' ')
    ) : '';

    // 1. 获取原始坐标（只在地址变化时执行）
    useEffect(() => {
        const fetchOriginalCoordinates = async () => {
            // 1.1 优先使用数据库中的坐标（参考 AddressLocationIncorrectButton 逻辑）
            let lat: number | null = deliveryAddress?.latitude != null ? Number(deliveryAddress.latitude) : null;
            let lng: number | null = deliveryAddress?.longitude != null ? Number(deliveryAddress.longitude) : null;

            // 1.2 如果数据库坐标不存在且有地址，尝试地理编码
            if ((lat == null || lng == null) && geocodingAddress) {
                try {
                    const response = await fetch(
                        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(geocodingAddress)}.json?access_token=${DEFAULT_MAPBOX_API_KEY}&limit=1`
                    );
                    const data = await response.json();
                    if (data.features?.[0]?.center) {
                        const [mapboxLng, mapboxLat] = data.features[0].center;
                        lat = mapboxLat;
                        lng = mapboxLng;
                        console.log('AddressCoordinateFeedbackNativePage - Geocoded coordinates from address (fallback):', { lat, lng });
                    }
                } catch (error) {
                    console.error('AddressCoordinateFeedbackNativePage - Geocoding failed:', error);
                }
            }

            if (lat != null && lng != null) {
                setOriginalLatitude(lat);
                setOriginalLongitude(lng);
            }
        };

        fetchOriginalCoordinates();
    }, [deliveryAddress, geocodingAddress]);

    // 2. 初始化选中位置（只在原始坐标刚拿到且用户未选点时执行）
    useEffect(() => {
        if (originalLatitude != null && originalLongitude != null) {
            // 只有当 selectedLatitude 和 selectedLongitude 还没有被设置时才初始化
            if (selectedLatitude === null && selectedLongitude === null) {
                setSelectedLatitude(originalLatitude);
                setSelectedLongitude(originalLongitude);
            }
        }
    }, [originalLatitude, originalLongitude]);

    // 使用默认缩放层级15
    const zoom = 15;
    // 从deliveryTaskOrder获取stopNumber
    const stopNumber = deliveryTaskOrder ? formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder) : null;

    const handleBack = () => {
        // 通过 URL scheme 触发 Native 返回上一页
        window.location.href = 'swiftx://nativePopBack';
    };

    const handleCoordinateChange = (lat: number, lng: number) => {
        setSelectedLatitude(lat);
        setSelectedLongitude(lng);
    };

    const handleSubmit = async () => {
        // 如果 orderAddressId 不可用，尝试从 deliveryTaskOrder 获取
        const finalOrderAddressId = orderAddressId ?? deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id;
        if (!finalOrderAddressId) {
            Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.orderAddressIncomplete'), position: 'center' });
            return;
        }

        if (!selectedLatitude || !selectedLongitude) {
            Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.pleaseSelectPosition'), position: 'center' });
            return;
        }

        if (!selectedTypeItem) {
            Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.pleaseSelectAddressType'), position: 'center' });
            return;
        }

        // 检查位置是否更新（距离必须在10米到20公里之间）
        if (originalLatitude && originalLongitude) {
            const distance = calculateDistance(
                originalLatitude,
                originalLongitude,
                selectedLatitude,
                selectedLongitude
            );
            if (distance < 10) {
                Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.distanceTooShort'), position: 'center' });
                return;
            }
            if (distance > 20000) {
                Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.distanceTooLong'), position: 'center' });
                return;
            }
        }

        // 防止重复提交：如果正在提交中，直接返回
        if (isSubmitting) {
            return;
        }

        try {
            // 生成或复用请求唯一ID，确保弱网重复调用时使用相同的 requestId
            // 如果还没有生成 requestId，则生成一个新的；如果已有，则复用（防止重复提交）
            if (!currentRequestIdRef.current) {
                currentRequestIdRef.current = `feedback_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
            }
            const requestId = currentRequestIdRef.current;

            const input: CreateAddressCoordinateFeedbackInput = {
                orderAddressId: finalOrderAddressId,
                originalLatitude: originalLatitude ?? null,
                originalLongitude: originalLongitude ?? null,
                feedbackLatitude: selectedLatitude,
                feedbackLongitude: selectedLongitude,
                requestId: requestId,
                trackingNumber: trackingNumber ?? deliveryTaskOrder?.trackingNumber ?? null,
                driverLatitude: driverLatitude,
                driverLongitude: driverLongitude,
                primaryTypeCode: selectedTypeItem.primaryCode,
                secondaryTypeCode: selectedTypeItem.secondaryCode
            };

            await createFeedback({
                variables: { input }
            });

            // 提交成功后，清除 requestId，允许下次提交
            currentRequestIdRef.current = null;
            Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.thankYouFeedback'), position: 'center' });
            // Toast 显示 2 秒后自动返回上一页
            setTimeout(() => {
                handleBack();
            }, 2000);
        } catch (error) {
            // 提交失败后，清除 requestId，允许重试
            currentRequestIdRef.current = null;
            console.error('提交反馈失败:', error);

            // 如果是 GraphQL 错误，说明已经被全局 errorLink 处理了，不需要再显示通用错误消息
            if (error instanceof ApolloError && error.graphQLErrors && error.graphQLErrors.length > 0) {
                // GraphQL 错误已经被全局 errorLink 处理，不再显示通用错误消息
                return;
            }

            // 非 GraphQL 错误（如网络错误等），显示通用错误消息
            Toast.show({ content: t('pages.delivery.addressCoordinateFeedback.submitFailed'), position: 'center' });
        }
    };

    // 计算两点间距离（米）
    const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
        const R = 6371000; // 地球半径（米）
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    // 如果坐标信息不可用，显示加载状态
    const hasCoordinates = originalLatitude != null && originalLongitude != null;
    const isLoading = !hasCoordinates && (isDataLoading || !deliveryTaskOrder || !deliveryAddress);
    const displayLatitude = selectedLatitude ?? originalLatitude;
    const displayLongitude = selectedLongitude ?? originalLongitude;
    const coordinateText = (displayLatitude != null && displayLongitude != null)
        ? `${displayLatitude.toFixed(7)}, ${displayLongitude.toFixed(7)}`
        : t('pages.delivery.addressCoordinateFeedback.notSelected');

    if (isLoading) {
        return (
            <div className="flex flex-col h-screen">
                <div className="flex-1 flex items-center justify-center">
                    <p>{t('pages.delivery.addressCoordinateFeedback.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen">
            {/* Map */}
            {originalLatitude != null && originalLongitude != null && (
                <div className="flex-1 flex flex-col min-h-0">
                    <div className="flex-1 relative">
                        <AddressCoordinateFeedbackMapView
                            latitude={selectedLatitude ?? originalLatitude}
                            longitude={selectedLongitude ?? originalLongitude}
                            originalLatitude={originalLatitude}
                            originalLongitude={originalLongitude}
                            onCoordinateChange={handleCoordinateChange}
                            showCenterIndicator={true}
                            enableDragToSelect={true}
                            zoom={zoom}
                            stopNumber={stopNumber}
                        />
                    </div>
                </div>
            )}

            {/* Form Card */}
            <Card className="flex-0 m-2 rounded-lg">
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col items-start w-full gap-2">
                        <p className="text-sm font-medium text-gray-600">
                            {t('pages.delivery.addressCoordinateFeedback.addressType')}
                            <span style={{ color: '#cf1322', marginLeft: 4 }}>*</span>
                        </p>
                        <div
                            onClick={() => setTypePickerVisible(true)}
                            className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-base leading-6 cursor-pointer flex items-center justify-between"
                        >
                            <span className={selectedTypeLabel ? 'text-[#222]' : 'text-[#999]'}>
                                {selectedTypeLabel || t('pages.delivery.addressCoordinateFeedback.selectPlaceholder')}
                            </span>
                            <DownOutline fontSize={18} color="#999" />
                        </div>
                    </div>
                    <div className="flex flex-col items-start w-full gap-2">
                        <p className="text-sm font-medium text-gray-600">
                            {t('pages.delivery.addressCoordinateFeedback.currentCoordinate')}
                        </p>
                        <div className="w-full bg-gray-100 rounded-md px-3 py-2 text-base text-gray-700 leading-6 break-words cursor-text select-text">
                            {coordinateText}
                        </div>
                    </div>
                    <div className="flex flex-col items-start w-full gap-2">
                        <p className="text-sm font-medium text-gray-600">
                            {t('pages.delivery.addressCoordinateFeedback.address')}
                        </p>
                        <div className="w-full bg-gray-100 rounded-md px-3 py-2 text-base text-gray-700 leading-6 break-words cursor-text select-text">
                            {address || '-'}
                        </div>
                    </div>
                </div>
            </Card>

            <Picker
                columns={typePickerColumns}
                visible={typePickerVisible}
                value={selectedSecondaryCode ? [selectedSecondaryCode] : []}
                onClose={() => setTypePickerVisible(false)}
                onConfirm={(value) => {
                    const code = (value?.[0] as string | undefined) ?? null;
                    setSelectedSecondaryCode(code);
                }}
                cancelText={t('pages.delivery.addressCoordinateFeedback.cancel')}
                confirmText={t('pages.delivery.addressCoordinateFeedback.confirm')}
            />

            {/* Bottom Buttons */}
            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <Button
                    color="default"
                    onClick={handleBack}
                    block
                    style={{
                        '--background-color': 'gray',
                        '--text-color': 'white'
                    }}
                >
                    {t('pages.delivery.addressCoordinateFeedback.cancel')}
                </Button>
                <Button
                    color="primary"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    block
                >
                    {t('pages.delivery.addressCoordinateFeedback.confirm')}
                </Button>
            </div>
        </div>
    );
};

export default AddressCoordinateFeedbackNativePage;


